import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiShoppingCart,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiArrowRight,
} from "react-icons/fi";
import { useAppContext } from "../AppContext";
import { MyDomain } from "../App";

function getGenderInfo(gender) {
  switch ((gender || "").toLowerCase()) {
    case "male":
    case "men":
      return { symbol: "♂", label: "Men", color: "#3b82f6" };
    case "female":
    case "women":
      return { symbol: "♀", label: "Women", color: "#ec4899" };
    default:
      return { symbol: "⚥", label: "Unisex", color: "#8b5cf6" };
  }
}

function normalizeProduct(p) {
  const hasDiscount = !!(
    p.priceAfterDiscount &&
    p.price &&
    p.priceAfterDiscount < p.price
  );
  return {
    id: p.id,
    name: p.name || "",
    image: p.image
      ? p.image.startsWith("http")
        ? p.image
        : `${MyDomain}${p.image}`
      : "",
    price: hasDiscount ? p.priceAfterDiscount : p.price || 0,
    originalPrice: hasDiscount ? p.price : null,
    hasDiscount,
    gender: p.gender || "",
  };
}

export default function Cart() {
  const navigate = useNavigate();
  const { User, cartItems, setCartItems } = useAppContext();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  const user =
    User ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem("user")) || null;
      } catch {
        return null;
      }
    })();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const ids = Object.keys(cartItems).map(Number).filter(Boolean);
    if (ids.length === 0) {
      setLoading(false);
      return;
    }

    Promise.all(
      ids.map((id) =>
        fetch(`${MyDomain}Products2/GetProduct/${id}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ),
    ).then((results) => {
      const map = {};
      results.filter(Boolean).forEach((p) => {
        map[p.id] = normalizeProduct(p);
      });
      setProducts(map);
      setLoading(false);
    });
  }, [cartItems, user]);

  const handleUpdateCart = async (productId, newAmt) => {
    try {
      if (newAmt <= 0) {
        await fetch(`${MyDomain}Products2/RemoveFromCart`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id, productId }),
        });
        setCartItems((prev) => {
          const n = { ...prev };
          delete n[productId];
          return n;
        });
      } else {
        await fetch(`${MyDomain}Products2/UpdateCartAmount`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id, productId, amount: newAmt }),
        });
        setCartItems((prev) => ({ ...prev, [productId]: newAmt }));
      }
    } catch (err) {
      console.error("Cart update failed", err);
    }
  };

  const cartEntries = Object.entries(cartItems)
    .map(([id, qty]) => ({
      id: Number(id),
      qty,
      product: products[Number(id)],
    }))
    .filter((e) => e.product);

  const subtotal = cartEntries.reduce(
    (sum, e) => sum + e.product.price * e.qty,
    0,
  );

  /* ── Not logged in ── */
  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "100px 20px",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(225,29,72,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FiShoppingCart size={32} color="#e11d48" />
        </div>
        <p
          style={{ color: "#1a1a1a", fontSize: 18, fontWeight: 700, margin: 0 }}
        >
          Please login first
        </p>
        <p style={{ color: "#999", fontSize: 14, margin: 0 }}>
          Login to view your cart.
        </p>
        <button
          onClick={() => navigate("/login")}
          style={{
            marginTop: 8,
            background: "#e11d48",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "12px 32px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Login
        </button>
      </div>
    );
  }

  /* ── Loading ── */
  if (loading) {
    return (
      <div
        style={{
          padding: "80px 0",
          textAlign: "center",
          color: "#999",
          fontSize: 15,
        }}
      >
        Loading cart…
      </div>
    );
  }

  /* ── Empty ── */
  if (cartEntries.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "100px 20px",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(225,29,72,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FiShoppingCart size={32} color="#e11d48" />
        </div>
        <p
          style={{ color: "#1a1a1a", fontSize: 18, fontWeight: 700, margin: 0 }}
        >
          Your cart is empty
        </p>
        <p style={{ color: "#999", fontSize: 14, margin: 0 }}>
          Add some products to get started.
        </p>
        <button
          onClick={() => navigate("/")}
          style={{
            marginTop: 8,
            background: "#e11d48",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "12px 32px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Browse Products
        </button>
      </div>
    );
  }

  /* ── Cart ── */
  return (
    <div style={{ padding: "28px 0 60px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            color: "#1a1a1a",
            fontSize: 24,
            fontWeight: 700,
            margin: "0 0 4px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FiShoppingCart size={22} color="#e11d48" />
          My Cart
        </h1>
        <p style={{ color: "#999", fontSize: 13, margin: 0 }}>
          {cartEntries.length} {cartEntries.length === 1 ? "item" : "items"}
        </p>
      </div>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* ── Items list ── */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {cartEntries.map(({ id, qty, product }) => {
            const g = product.gender ? getGenderInfo(product.gender) : null;
            return (
              <div
                key={id}
                style={{
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 14,
                  padding: "16px",
                  display: "flex",
                  gap: 16,
                  alignItems: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                {/* Image */}
                <div
                  onClick={() => navigate(`/product/${id}`)}
                  style={{
                    flexShrink: 0,
                    width: 88,
                    height: 88,
                    borderRadius: 10,
                    overflow: "hidden",
                    background: "#f9f9f9",
                    border: "1px solid rgba(0,0,0,0.07)",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    onError={(e) => {
                      e.target.src =
                        "https://placehold.co/88x88/f5f5f5/ccc?text=No+Image";
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    onClick={() => navigate(`/product/${id}`)}
                    style={{
                      color: "#1a1a1a",
                      fontSize: 14,
                      fontWeight: 600,
                      margin: "0 0 6px",
                      lineHeight: 1.4,
                      cursor: "pointer",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {product.name}
                  </p>

                  {g && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        fontSize: 11,
                        fontWeight: 700,
                        color: g.color,
                        marginBottom: 8,
                      }}
                    >
                      {g.symbol} {g.label}
                    </span>
                  )}

                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        color: "#1a1a1a",
                        fontSize: 15,
                        fontWeight: 700,
                      }}
                    >
                      ₪{product.price}
                    </span>
                    {product.hasDiscount && product.originalPrice && (
                      <span
                        style={{
                          color: "#bbb",
                          fontSize: 12,
                          textDecoration: "line-through",
                        }}
                      >
                        ₪{product.originalPrice}
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: qty + subtotal + remove */}
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 12,
                  }}
                >
                  {/* Qty controls */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1.5px solid rgba(0,0,0,0.12)",
                      borderRadius: 30,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      onClick={() => handleUpdateCart(id, qty - 1)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#555",
                        transition: "background 0.2s",
                      }}
                    >
                      <FiMinus size={14} />
                    </button>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#1a1a1a",
                        minWidth: 28,
                        textAlign: "center",
                      }}
                    >
                      {qty}
                    </span>
                    <button
                      onClick={() => handleUpdateCart(id, qty + 1)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#555",
                        transition: "background 0.2s",
                      }}
                    >
                      <FiPlus size={14} />
                    </button>
                  </div>

                  {/* Subtotal */}
                  <span
                    style={{ fontSize: 15, fontWeight: 700, color: "#e11d48" }}
                  >
                    ₪{(product.price * qty).toFixed(2)}
                  </span>

                  {/* Remove */}
                  <button
                    onClick={() => handleUpdateCart(id, 0)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#ccc",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      transition: "color 0.2s",
                      padding: 0,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.color = "#e11d48")
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#ccc")}
                  >
                    <FiTrash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Order summary ── */}
        <div
          style={{
            width: 300,
            flexShrink: 0,
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 14,
            padding: "20px 20px 24px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            position: "sticky",
            top: 24,
          }}
        >
          <h2
            style={{
              color: "#1a1a1a",
              fontSize: 16,
              fontWeight: 700,
              margin: "0 0 20px",
            }}
          >
            Order Summary
          </h2>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 20,
            }}
          >
            {cartEntries.map(({ id, qty, product }) => (
              <div
                key={id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: 13,
                  color: "#555",
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 160,
                  }}
                >
                  {product.name}
                </span>
                <span
                  style={{ flexShrink: 0, fontWeight: 600, color: "#1a1a1a" }}
                >
                  {qty} × ₪{product.price}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              borderTop: "1px solid rgba(0,0,0,0.08)",
              paddingTop: 16,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>
                Total
              </span>
              <span style={{ fontSize: 20, fontWeight: 800, color: "#e11d48" }}>
                ₪{subtotal.toFixed(2)}
              </span>
            </div>
          </div>

          <button
            style={{
              width: "100%",
              background: "#e11d48",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "14px 0",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            onClick={() => navigate("/checkout")}
          >
            Checkout <FiArrowRight size={16} />
          </button>

          <button
            onClick={() => navigate("/")}
            style={{
              width: "100%",
              background: "none",
              color: "#666",
              border: "1.5px solid rgba(0,0,0,0.12)",
              borderRadius: 12,
              padding: "11px 0",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              marginTop: 10,
              transition: "border-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "#e11d48")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)")
            }
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
}
